import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { mockAuthProvider } from '@/services/auth/MockAuthProvider';
import { ROUTES } from '@/constants/routes';

export const MfaPage: React.FC = () => {
  const navigate = useNavigate();
  const [otpCode, setOtpCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    const result = await mockAuthProvider.verifyMfa({ userId: 'USR-109', otpCode });
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.DASHBOARD);
    } else {
      setErrorMessage(result.errorMessage || 'Invalid authentication code.');
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background p-4 sm:p-6">
      <div className="w-full max-w-md space-y-6 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xl text-center">
        <button
          onClick={() => navigate(ROUTES.AUTH.LOGIN)}
          className="flex items-center gap-1.5 text-xs font-semibold text-foreground-secondary hover:text-foreground text-left"
        >
          <ArrowLeft className="h-4 w-4" /> Cancel & Return to Sign In
        </button>

        <div className="flex flex-col items-center space-y-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Multi-Factor Authentication</h1>
          <p className="text-xs text-foreground-secondary leading-relaxed">
            Enter the 6-digit verification code generated by your enterprise authenticator app (Demo Code: <span className="font-mono font-bold text-foreground">123456</span>).
          </p>
        </div>

        {errorMessage && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive text-left">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <input
            type="text"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            placeholder="123456"
            className="w-full text-center font-mono text-2xl font-bold tracking-widest rounded-md border border-input bg-surface py-3 focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <Button type="submit" className="w-full" isLoading={isLoading} disabled={otpCode.length !== 6}>
            Verify Authentication Code
          </Button>
        </form>

        <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-foreground-muted">
          <span>Didn't receive a code?</span>
          <button
            onClick={() => setErrorMessage('A new OTP challenge code has been dispatched.')}
            className="text-primary hover:underline font-bold flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" /> Resend Code
          </button>
        </div>
      </div>
    </div>
  );
};
